import React from 'react';
import { BarChart2, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты вычетов для сравнения.
          После расчета вычета нажмите кнопку "Сохранить расчет".
        </p>
      </div>
    );
  }

  // Получение названия типа вычета
  const getDeductionName = (typeId) => {
    const deductionNames = {
      property: 'Имущественный',
      mortgage: 'Ипотечный',
      medical: 'Медицинский',
      education: 'Образовательный',
      investment: 'Инвестиционный',
      charity: 'Благотворительный',
      comprehensive: 'Комплексный'
    };
    return deductionNames[typeId] || 'Другой';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип вычета:</span>
                <span className="font-medium">
                  {getDeductionName(calc.formData.deductionType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Сумма вычета:</span>
                <span>
                  {formatCurrency(calc.results.totalAvailableDeduction)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ставка НДФЛ:</span>
                <span>
                  {calc.formData.taxRate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Возврат НДФЛ:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(calc.results.totalTaxReturn)}
                </span>
              </div>
              {calc.results.yearsToComplete > 1 && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Полный возврат за {calc.results.yearsToComplete} {pluralizeYears(calc.results.yearsToComplete)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length > 1 && (
        <div className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Сравнение вычетов:</h4>
          
          <div className="space-y-4">
            {/* Сравнение сумм возврата */}
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Сумма возврата НДФЛ:</p>
              {savedCalculations.map(calc => {
                // Находим максимальную сумму возврата среди всех расчетов
                const maxReturn = Math.max(...savedCalculations.map(c => c.results.totalTaxReturn));
                // Вычисляем процент от максимальной суммы
                const percentOfMax = (calc.results.totalTaxReturn / maxReturn) * 100;
                
                return (
                  <div key={`return-${calc.id}`} className="space-y-1 mb-2">
                    <div className="flex justify-between text-xs">
                      <span className="truncate max-w-[150px]" title={calc.name}>{calc.name}</span>
                      <span>{formatCurrency(calc.results.totalTaxReturn)}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-emerald-500 dark:bg-emerald-400"
                        style={{ width: `${Math.max(4, percentOfMax)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Сравнение сроков получения */}
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Срок получения вычета:</p>
              {savedCalculations.map(calc => {
                // Находим максимальный срок среди всех расчетов
                const maxYears = Math.max(...savedCalculations.map(c => c.results.yearsToComplete));
                // Вычисляем процент от максимального срока (инвертированный)
                const percentOfMax = 100 - ((calc.results.yearsToComplete / maxYears) * 100) + 20;
                
                return (
                  <div key={`years-${calc.id}`} className="space-y-1 mb-2">
                    <div className="flex justify-between text-xs">
                      <span className="truncate max-w-[150px]" title={calc.name}>{calc.name}</span>
                      <span>{calc.results.yearsToComplete} {pluralizeYears(calc.results.yearsToComplete)}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          calc.results.yearsToComplete === 1 
                            ? 'bg-green-500 dark:bg-green-400' 
                            : calc.results.yearsToComplete <= 3 
                              ? 'bg-yellow-500 dark:bg-yellow-400' 
                              : 'bg-orange-500 dark:bg-orange-400'
                        }`}
                        style={{ width: `${Math.max(20, percentOfMax)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const pluralizeYears = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ComparisonSidebar;